/**
 * AI features in federal mode — ADR-0073 §7 (V105-2-7), Settings → Security #ai-features.
 *
 * GET /federal-parity (any member). Shown when the workspace is in federal mode
 * or still on the legacy in-boundary flag: per feature, what runs for its data
 * now. A feature that is off always carries its action inline (no dead end);
 * members who can't take it get who to ask.
 */
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Spinner } from '@aigovops/design-system';
import { canTakeAction, useFederalParity } from '../lib/federal-parity';
import type { FeatureParity } from '../lib/api';
import { AskAdmin } from './AskAdmin';
import './data-protection-settings.css';

const STATE: Record<FeatureParity['state'], { label: string; tone: 'accent' | 'neutral' }> = {
  ai: { label: 'AI', tone: 'accent' }, deterministic: { label: 'Built-in', tone: 'neutral' }, off: { label: 'Off', tone: 'neutral' },
};

export function FederalParityStatus({ slug, role }: { slug: string; role: string }) {
  const q = useFederalParity(slug);
  if (q.isLoading) return <Spinner label="Loading AI features" />;
  if (q.error || !q.data) {
    return <Alert variant="error"><p>AI features could not load.</p><Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>;
  }
  if (q.data.data_mode !== 'federal' && !q.data.federal_boundary_only) return null;
  const blockedForMe = q.data.features.some((f) => f.state === 'off' && f.action && !canTakeAction(role, f.action));
  return (
    <div id="ai-features" className="dps" tabIndex={-1} role="region" aria-labelledby="dps-parity-h">
      <Card>
        <div className="dps-body">
          <h2 id="dps-parity-h" className="dps-h">AI features</h2>
          <ul className="dps-list" aria-label="AI features">
            {q.data.features.map((f) => (
              <li key={f.feature} className="dps-item">
                <span>{f.label}</span>
                <Badge tone={STATE[f.state].tone}>{STATE[f.state].label}</Badge>
                {f.state === 'off' && f.action && canTakeAction(role, f.action)
                  ? <Link to={f.action.href} aria-label={`${f.action.label} · ${f.label}`}>{f.action.label}</Link>
                  : <span aria-hidden="true" />}
              </li>
            ))}
          </ul>
          {blockedForMe ? <AskAdmin slug={slug} what="connects an AI provider for this workspace's data" subject="Turn AI features back on for our data" /> : null}
        </div>
      </Card>
    </div>
  );
}
